import { MEMBERSHIP_PLAN_IDS } from './catalog';
import { hasEntitlement, ownsProduct } from './entitlements';
import type { EntitlementsMap, MembershipPlanId, ProductId } from './types';

/**
 * Display order for the plan picker. Monthly first: it is the lower-commitment
 * entry point, and the annual card carries the savings callout.
 */
export const MEMBERSHIP_PLAN_ORDER: MembershipPlanId[] = MEMBERSHIP_PLAN_IDS;

/** The plan a compact upsell CTA offers when someone has no membership. */
export const DEFAULT_PLAN_ID: MembershipPlanId = 'membership_monthly';

/** True when the assignment holds everything a membership grants. */
export function hasFullProgram(entitlements: EntitlementsMap): boolean {
  return ownsProduct(entitlements, DEFAULT_PLAN_ID);
}

/**
 * The plan to offer right now, or null when there is nothing left to sell.
 *
 * Both plans grant the same thing, so this is "should we show a buy CTA at
 * all", not a position in a tier ladder. Note that entitlements alone cannot
 * tell you whether a membership is still *active* - an expired member keeps
 * their flags. Callers that need live status read `membership` off the
 * assignment instead.
 */
export function getOfferedPlan(entitlements: EntitlementsMap): MembershipPlanId | null {
  return hasFullProgram(entitlements) ? null : DEFAULT_PLAN_ID;
}

/**
 * Per-product post-upgrade destination. Assignment-scoped content routes do not
 * exist yet, so upgrades currently land on the assignment hub with an
 * `upgraded` flag; each product gets its own entry here so destinations can be
 * specialized later without touching the checkout call sites.
 */
const UPGRADE_DESTINATION: Partial<Record<ProductId, (assignmentId: number) => string>> = {};

function upgradeDestination(productId: ProductId, assignmentId: number): string {
  const custom = UPGRADE_DESTINATION[productId];
  if (custom) {
    return custom(assignmentId);
  }
  return `/account/payments/${assignmentId}?upgraded=${productId}`;
}

/**
 * Decide where a buyer should land after a successful checkout, from the
 * assignment's PRE-purchase entitlement state and the product being bought.
 *
 * - New assignment (none selected): returns undefined; the confirm handler on the
 *   subscriptions page routes to the freshly created assignment's onboarding.
 * - Entry purchase on an existing empty assignment (no `pre_assessment` yet):
 *   onboarding, so the athlete can complete setup and take the assessment.
 * - Resubscribe (already has `pre_assessment`): a per-product destination
 *   (assignment hub with an `upgraded` flag today).
 */
export function resolveCheckoutReturnTo(params: {
  entitlements: EntitlementsMap;
  productId: ProductId;
  assignmentId?: number;
}): string | undefined {
  const { entitlements, productId, assignmentId } = params;
  if (assignmentId === undefined) {
    return undefined;
  }
  const isUpgrade = hasEntitlement(entitlements, 'pre_assessment');
  if (isUpgrade) {
    return upgradeDestination(productId, assignmentId);
  }
  return `/assignments/${assignmentId}/onboarding`;
}
